import type { ProcedureStep, Stored, User } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	UserArrayEqualsFilterModifier,
	UserArrayEveryFilterModifier,
	UserArrayNoneFilterModifier,
	UserArrayNotEqualsFilterModifier,
	UserArraySomeFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureStepAssigneeFilterCell } from "./ProcedureStepAssigneeFilterCell"

type ProcedureStepAssigneeFieldValue = User["id"][]
type ProcedureStepAssigneeFilterValue = User["id"][]

export class ProcedureStepAssigneeFilterDef extends BaseFilterDef<
	ProcedureStepAssigneeFieldValue,
	ProcedureStepAssigneeFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: ProcedureStepAssigneeFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly procedureStep: Stored<ProcedureStep>
	readonly users: User[]

	constructor(procedureStep: Stored<ProcedureStep>, users: User[]) {
		super()
		this.procedureStep = procedureStep
		this.users = users
		this.id = `procedure-step-assignees:${procedureStep.uuid}`
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: UserArrayEqualsFilterModifier,
			isValueValid: (value: ProcedureStepAssigneeFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepAssigneeFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: UserArrayNotEqualsFilterModifier,
			isValueValid: (value: ProcedureStepAssigneeFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepAssigneeFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		every: createFilterModifierConfig({
			modifier: UserArrayEveryFilterModifier,
			isValueValid: (value: ProcedureStepAssigneeFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepAssigneeFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		some: createFilterModifierConfig({
			modifier: UserArraySomeFilterModifier,
			isValueValid: (value: ProcedureStepAssigneeFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepAssigneeFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		none: createFilterModifierConfig({
			modifier: UserArrayNoneFilterModifier,
			isValueValid: (value: ProcedureStepAssigneeFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepAssigneeFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureStepAssigneeFilterDef>): ReactNode {
		return <ProcedureStepAssigneeFilterCell key={props.filter.id} {...props} />
	}
}
